import * as React from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import * as moment from 'moment';

import * as Edge from '../../../core';
import FormField from '../../../components/formField';
import Input from '../../../components/global/input';
import Error from '../../../components/global/error';
import ConfigLayout from '../configLayout';
import { FullScreenService } from '../../../services/fullScreenService';

export interface ContrastTrackingConfigFormProps {
	mode: Edge.Models.ExerciseMode;
	initialConfiguration: Partial<Edge.Models.ContrastTrackingExerciseConfiguration>;
	lockConfiguration: boolean;
	onSubmit: (configuration: Edge.Models.ContrastTrackingExerciseConfiguration, fullScreen: boolean) => Promise<void>;

	operationName: string;
}

export interface ContrastTrackingConfigFormValues {
	durationSeconds: string;
	fullScreen: boolean;
	level: string;
	size: string;
	speed: string;
	trackingMode: string;
}

const schema = Yup.object().shape({
	durationSeconds: Yup.number(),
	fullScreen: Yup.boolean(),
	level: Yup.number().required('Level is required'),
	size: Yup.string().required('Circle Size is required'),
	speed: Yup.string().required('Circle Speed is required'),
	trackingMode: Yup.string().required('Tracking Mode is required'),
});

interface InstructionProps {
	config: ContrastTrackingConfigFormValues;
	startText: string;
}

const instructions = ({ config, startText }: InstructionProps) => (
	<ul>
		<li>Red/Blue glasses are NOT worn during this exercise.</li>
		<li>After clicking the '{startText}' button four circles will appear; one in every quadrant.</li>
		<li>
			Identify which circle is different by pressing the arrow key based on where it is located (up, down, left,
			or right), as fast as possible. An on-screen D-Pad will appear if touch controls are enabled.
		</li>
		<li>
			The contrast and difficulty will increase as you progress through each station. In order to move onto the
			next station, you will need to get two out of three correct.
		</li>
		<li>
			If you get two out of three incorrect the station will revert by one, decreasing contrast and difficulty.
		</li>
		<li>This exercise is scored based on speed, accuracy and maximum station achieved.</li>
		<li>Saccades tracking mode will have you follow the circles as they move from spot to spot.</li>
		<li>Pursuits tracking mode will have you follow the circles in a smooth pattern.</li>
		<li>
			This exercise will last {moment.duration(parseInt(config.durationSeconds), 'seconds').humanize()} and you
			have <strong>5 seconds</strong> to answer each round.
		</li>
	</ul>
);

export default class ContrastTrackingConfigForm extends React.Component<ContrastTrackingConfigFormProps> {
	public render() {
		const { initialConfiguration, lockConfiguration, onSubmit, operationName, mode } = this.props;
		const startText = mode === 'evaluate' ? 'Start Exercise' : 'Train Contrast Tracking';

		// Spaces out a string value when a capital letter is found.
		const spaceOutWords = (value: string): string => value.replace(/([A-Z])/g, ' $1').trim();
		const presence = (value: number | undefined, fallback: number): number =>
			typeof value !== 'undefined' ? value : fallback;

		return (
			<Formik
				initialValues={Object.assign(
					{},
					{
						/** anything not specified here won't show an error message after an attempted submit */
						durationSeconds: presence(initialConfiguration.durationSeconds, 60).toString(),
						fullScreen: false,
						level: presence(initialConfiguration.level, 1).toString(),
						size: presence(initialConfiguration.size, Edge.Models.ExerciseSize.Medium).toString(),
						speed: presence(initialConfiguration.speed, Edge.Models.ExerciseSpeed.None).toString(),
						trackingMode: presence(
							initialConfiguration.trackingMode,
							Edge.Models.TrackingMode.Saccades
						).toString(),
					}
				)}
				validationSchema={schema}
				onSubmit={async (values, actions) => {
					actions.setStatus(undefined);
					try {
						await onSubmit(
							{
								durationSeconds: parseInt(values.durationSeconds),
								exerciseTypeId: Edge.Models.ExerciseTypeId.ContrastTracking,
								id: initialConfiguration.id,
								level: parseInt(values.level),
								size: parseInt(values.size),
								speed: parseInt(values.speed),
								trackingMode: parseInt(values.trackingMode),
							},
							values.fullScreen
						);
					} catch (e) {
						actions.setStatus(Edge.API.getErrorMessage(e));
					}
					actions.setSubmitting(false);
				}}
				render={(props: FormikProps<ContrastTrackingConfigFormValues>) => (
					<Form>
						<ConfigLayout
							operationName={operationName}
							exerciseName="Contrast Tracking"
							headerActions={
								<>
									<Input type="submit" disabled={props.isSubmitting} value={startText} />
									{props.status && <Error>{props.status}</Error>}
									{FullScreenService.isEnabled() && (
										<FormField type="checkbox" name="fullScreen" description="View Full Screen" />
									)}
								</>
							}
							instructions={instructions({ config: props.values, startText })}
							settings={
								<>
									<FormField
										component="select"
										name="durationSeconds"
										description="Session Duration"
										disabled={lockConfiguration}
									>
										<option value="60">1 minute</option>
										<option value="120">2 minutes</option>
										<option value="180">3 minutes</option>
										<option value="300">5 minutes</option>
									</FormField>
									<FormField
										component="select"
										name="level"
										description="Level"
										disabled={lockConfiguration}
									>
										<option>1</option>
										<option>2</option>
										<option>3</option>
										<option>4</option>
									</FormField>
									<FormField
										component="select"
										name="size"
										description="Circle Size"
										disabled={lockConfiguration}
									>
										{Object.keys(Edge.Models.ExerciseSize)
											.filter((size: any) => typeof Edge.Models.ExerciseSize[size] === 'number')
											.map((size: any) => ({
												key: +Edge.Models.ExerciseSize[size],
												value: spaceOutWords(size),
											}))
											.filter((size) => size.key > 0)
											.map((size) => (
												<option key={size.key} value={size.key}>
													{size.value}
												</option>
											))}
									</FormField>
									<FormField
										component="select"
										name="trackingMode"
										description="Tracking Mode"
										disabled={lockConfiguration}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
											// Resets speed field every time tracking mode changes.
											const isPursuits =
												e.target.value === Edge.Models.TrackingMode.Pursuits.toString();

											props.setFieldValue(
												'speed',
												isPursuits
													? Edge.Models.ExerciseSpeed.Normal
													: Edge.Models.ExerciseSpeed.None
											);
										}}
									>
										{Object.keys(Edge.Models.TrackingMode)
											.filter((mode: any) => typeof Edge.Models.TrackingMode[mode] === 'number')
											.map((mode: any) => ({
												key: +Edge.Models.TrackingMode[mode],
												value: spaceOutWords(mode),
											}))
											.filter((mode) => mode.key > 0)
											.map((mode) => (
												<option key={mode.key} value={mode.key}>
													{mode.value}
												</option>
											))}
									</FormField>
									<FormField
										component="select"
										name="speed"
										description="Circle Speed"
										disabled={lockConfiguration}
									>
										{Object.keys(Edge.Models.ExerciseSpeed)
											.filter(
												(speed: any) => typeof Edge.Models.ExerciseSpeed[speed] === 'number'
											)
											.map((speed: any) => ({
												key: +Edge.Models.ExerciseSpeed[speed],
												value: spaceOutWords(speed),
											}))
											// Displays only "none" speed if Tracking Mode is Saccades.
											// Avoids "none", "very fast" otherwise.
											.filter((speed) =>
												parseInt(props.values.trackingMode) ===
												Edge.Models.TrackingMode.Saccades
													? speed.key === Edge.Models.ExerciseSpeed.None
													: speed.key > Edge.Models.ExerciseSpeed.None &&
													  speed.key < Edge.Models.ExerciseSpeed.VeryFast
											)
											.map((speed) => (
												<option key={speed.key} value={speed.key}>
													{speed.value}
												</option>
											))}
									</FormField>
								</>
							}
							videoUrl={Edge.Constants.VIDEOS.CONTRAST_TRACKING}
						/>
					</Form>
				)}
			/>
		);
	}
}
